/**
 * Strictly-necessary operational conversion analytics helpers for the community
 * surface (the "Option 7" page-flow model, extended from `requested_page` /
 * `viewed_page` to clicked-CTA / started-flow / submitted-conversion moments).
 *
 * These helpers assemble ONLY campaign, page and referrer context. They must
 * never carry identity. For these ad-conversion operational events we
 * deliberately omit `sn_id` and `anonymousId` in addition to the usual identity
 * fields — see {@link DENIED_IDENTITY_KEYS} and {@link assertNoIdentityFields}.
 */

import { Attributes } from '@atlassiansox/analytics-web-client/dist/types/types';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

type UtmKey = (typeof UTM_KEYS)[number];

export type CampaignContext = Partial<Record<UtmKey, string>>;

export interface ReferrerContext {
  external_referrer?: string;
  internal_referrer_path?: string;
  landing_page_path?: string;
}

/**
 * Keys that must NEVER appear in the attributes of these ad-conversion
 * operational events. `sn_id` and `anonymousId` are normally permitted for
 * operational events, but the conversion-event requirements forbid any identity
 * or session-stitching signal, so we strip them at the repo level.
 */
export const DENIED_IDENTITY_KEYS: readonly string[] = [
  'aaid',
  'accountId',
  'account_id',
  'userId',
  'authenticatedUserId',
  'email',
  'emailId',
  'emailAddress',
  'anonymousId',
  'anonymous_id',
  'sn_id',
  'snId',
  'xid',
];

const isWindow = (): boolean => typeof window !== 'undefined';

const LANDING_PAGE_SESSION_KEY = 'community.conversion.landingPagePath';

/** Parse the standard UTM/campaign params from the current URL. */
export const getCampaignContext = (search?: string): CampaignContext => {
  if (!isWindow() && search === undefined) {
    return {};
  }

  const params = new URLSearchParams(search ?? window.location.search);
  const context: CampaignContext = {};

  UTM_KEYS.forEach((key) => {
    const value = params.get(key);

    if (value) {
      context[key] = value;
    }
  });

  return context;
};

const stripToPath = (value: string): string => {
  try {
    return new URL(value, window.location.origin).pathname;
  } catch {
    return value.split('?')[0].split('#')[0];
  }
};

/**
 * Resolve the landing page path for the current session. Persisted once in
 * sessionStorage so it survives in-session navigation but never crosses sessions.
 */
const resolveLandingPagePath = (): string | undefined => {
  if (!isWindow()) {
    return undefined;
  }

  const currentPath = window.location.pathname;

  try {
    const existing = window.sessionStorage.getItem(LANDING_PAGE_SESSION_KEY);

    if (existing) {
      return existing;
    }

    window.sessionStorage.setItem(LANDING_PAGE_SESSION_KEY, currentPath);

    return currentPath;
  } catch {
    return currentPath;
  }
};

/** Assemble referrer context (external referrer, internal path, landing path). */
export const getReferrerContext = (internalReferrerPath?: string | null): ReferrerContext => {
  const context: ReferrerContext = {};

  if (isWindow() && document.referrer) {
    let isExternal = true;

    try {
      isExternal = new URL(document.referrer).origin !== window.location.origin;
    } catch {
      isExternal = true;
    }

    if (isExternal) {
      context.external_referrer = document.referrer;
    }
  }

  if (internalReferrerPath) {
    context.internal_referrer_path = stripToPath(internalReferrerPath);
  }

  const landingPagePath = resolveLandingPagePath();

  if (landingPagePath) {
    context.landing_page_path = landingPagePath;
  }

  return context;
};

/**
 * Development/test-time guard. Throws if any denied identity key is present in
 * the attributes for an ad-conversion operational event.
 */
export const assertNoIdentityFields = (attributes: Attributes): void => {
  const offending = Object.keys(attributes).filter((key) => DENIED_IDENTITY_KEYS.includes(key));

  if (offending.length > 0) {
    throw new Error(
      `Conversion operational event attributes must not contain identity fields: ${offending.join(', ')}`,
    );
  }
};

/**
 * Derive a coarse page_type from a path for the community surface. Kept simple
 * and dependency-free; extend as new conversion surfaces are added.
 */
export const getCommunityPageType = (pathname: string): string => {
  if (pathname.includes('/champions')) {
    return 'championsScreen';
  }

  if (pathname === '/' || pathname === '') {
    return 'homeScreen';
  }

  return 'communityScreen';
};

export interface ConversionEventInput {
  eventName: string;
  conversionType: string;
  pagePath?: string;
  pageType?: string;
  ctaId?: string;
  ctaLabel?: string;
  internalReferrerPath?: string | null;
  extra?: Attributes;
}

/**
 * Build the shared + event-specific attribute bag for an ad-conversion
 * operational event on the community surface. Contains NO identity fields.
 */
export const buildConversionEventAttributes = (input: ConversionEventInput): Attributes => {
  const pagePath = input.pagePath ?? (isWindow() ? window.location.pathname : '');
  const pageType = input.pageType ?? getCommunityPageType(pagePath);

  const attributes: Attributes = {
    event_name: input.eventName,
    surface: 'community',
    page_path: pagePath,
    page_type: pageType,
    event_timestamp: new Date().toISOString(),
    conversion_type: input.conversionType,
    ...getCampaignContext(),
    ...getReferrerContext(input.internalReferrerPath),
    ...(input.ctaId ? { cta_id: input.ctaId } : {}),
    ...(input.ctaLabel ? { cta_label: input.ctaLabel } : {}),
    ...(input.extra ?? {}),
  };

  assertNoIdentityFields(attributes);

  return attributes;
};
